import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { omit } from 'ramda'
import { Button } from '@/components/button'
import { MessageModal } from '@/components/message-modal'
import { createModal } from '@/components/modalize/mui'
import { Label } from '@/components/text'
import { formatAddress } from '@/utils/listings'
import { colors, breakpoints } from '@/styles'
import type { Contract } from '@/features/Pad'
import DownloadIcon from './download.svg?react'
import { downloadPdf, getAllPdf } from '../download-pad-agreement'

const Container = styled.div`
  padding: 0 8px;
  width: 100%;
`

const ScrollContainer = styled.div`
  overflow-y: auto;
  max-height: 424px;
  width: 100%;
  border-top: 1px solid #dfe0e0;
  border-bottom: 1px solid #dfe0e0;
`

const List = styled.ul`
  width: 100%;
  padding: 0;
  margin: 0;
  > li:not(:last-of-type) {
    border-bottom: 1px solid #dfe0e0;
  }
`

const ListItem = styled.li`
  border: 0;
  width: 100%;
  padding: 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div:first-of-type {
    flex: 1;
    color: ${colors.grey56};
  }
`

const ButtonWrapper = styled.div`
  padding: 16px 0 12px;
  display: flex;
  justify-content: center;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    > button {
      display: none;
    }
  }
`

const IconWrapper = styled.div`
  width: 16px;
  height: 16px;
  position: relative;
`

const Icon = styled(DownloadIcon)`
  position: absolute;
  left: 0;
  top: -2px;
`

type ModalProps = {
  contracts?: Contract[]
  paymentIds?: string[]
}

// TODO We need to find a way to map payment data to the contract
const ConnectPadSuccess: React.FC<ModalProps> = ({ contracts = [], paymentIds = [] }) => {
  const { t } = useTranslation()
  const showDownloadAll = contracts.length > 1
  const downloadAllPdf = getAllPdf(paymentIds)
  const hasContracts = contracts.length > 0
  return (
    <MessageModal
      icon="round_check"
      title={t('r.payment_settings.edit.pad_payment_set.title', 'PAD Payment Set!')}
      message={t(
        'r.payment_settings.edit.pad_account_connected.text',
        'Congratulations! You have successfully submitted your pre-authorized debit information.'
      )}
      buttonText={t('r.pad_settings.edit.got_it.button', 'Got it!')}
    >
      {hasContracts && (
        <Container>
          <ScrollContainer>
            <List>
              {paymentIds.map((paymentId, index) => {
                const contract = contracts[index]
                return (
                  <ListItem key={`pad-pdf-${contract.listing_contract_id}`}>
                    <Label fontSize="14px">
                      {formatAddress(omit(['zip', 'state', 'city'], contract))}
                    </Label>{' '}
                    <Button theme="text" size="small" onClick={() => downloadPdf(paymentId)}>
                      {t(
                        'r.payment_settings.edit.pad_account_connected.download_pdf.button',
                        'Download PDF'
                      )}
                    </Button>
                  </ListItem>
                )
              })}
            </List>
          </ScrollContainer>
          <ButtonWrapper>
            {showDownloadAll && (
              <Button theme="text" size="small" onClick={downloadAllPdf}>
                <IconWrapper>
                  <Icon aria-hidden />
                </IconWrapper>
                {t(
                  'r.payment_settings.edit.pad_account_connected.download_all_pdf.button',
                  'Download All'
                )}
              </Button>
            )}
          </ButtonWrapper>
        </Container>
      )}
    </MessageModal>
  )
}

const show = createModal(ConnectPadSuccess, {
  width: '496px',
  clickOutside: false,
  showClose: false
})

export default show
